import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Plus, Search, X, Eye, Edit, Trash2, ChevronLeft, Save, Calendar,
  Package, Clock, CheckCircle, AlertCircle, AlertTriangle, FileText,
  Filter, RefreshCw, ClipboardList, BarChart3, Ship,
  Info, DollarSign, Truck, Hash, Layers,
} from 'lucide-react';
import { orderService } from '../services/orderService';
import { api } from '../config/api';
import Notification from './Notification';

// ─── Status colors ───────────────────────────────────────────────────────────
const FTY_PI_STATUS_COLORS = {
  po_in_process:        { bg: 'bg-blue-100 text-blue-800 border-blue-300',     dot: 'bg-blue-500'   },
  po_sent:              { bg: 'bg-blue-100 text-blue-800 border-blue-300',     dot: 'bg-blue-500'   },
  negotiating:          { bg: 'bg-amber-100 text-amber-800 border-amber-300',  dot: 'bg-amber-500'  },
  awaiting_final_pi:    { bg: 'bg-yellow-100 text-yellow-800 border-yellow-300', dot: 'bg-yellow-500' },
  awaiting_final_pi_wtb:{ bg: 'bg-yellow-100 text-yellow-800 border-yellow-300', dot: 'bg-yellow-500' },
  pending_revised:      { bg: 'bg-orange-100 text-orange-800 border-orange-300', dot: 'bg-orange-500' },
  confirmed:            { bg: 'bg-green-100 text-green-800 border-green-300',  dot: 'bg-green-500'  },
  standby:              { bg: 'bg-gray-100 text-gray-700 border-gray-300',     dot: 'bg-gray-400'   },
  cancelled:            { bg: 'bg-red-100 text-red-800 border-red-300',        dot: 'bg-red-500'    },
};

// ─── Países donde Orilat opera (curado) ──────────────────────────────────────
const COUNTRY_OPTIONS = [
  'Argentina', 'Brasil', 'Chile', 'Colombia', 'México', 'Paraguay',
  'Perú', 'Uruguay', 'Bolivia', 'Ecuador', 'Venezuela',
  'Estados Unidos', 'Canadá',
  'España', 'Italia', 'Francia', 'Alemania', 'Países Bajos', 'Reino Unido',
  'China', 'Hong Kong', 'India', 'Vietnam', 'Indonesia',
  'Australia', 'Nueva Zelanda',
];

// ─── Helpers ─────────────────────────────────────────────────────────────────
const fmtN = (v, d = 2) => {
  const n = parseFloat(v);
  if (Number.isNaN(n)) return '-';
  return n.toLocaleString('es-AR', { minimumFractionDigits: d, maximumFractionDigits: d });
};

const fmtDate = (v) => {
  if (!v) return '-';
  try {
    const d = typeof v === 'string' && v.length === 10 ? new Date(v + 'T00:00:00') : new Date(v);
    return d.toLocaleDateString('es-AR');
  } catch { return v; }
};

const diffDays = (d1, d2) => {
  if (!d1 || !d2) return null;
  const a = new Date(d1);
  const b = new Date(d2);
  return Math.round((a - b) / (1000 * 60 * 60 * 24));
};

const formatDayDelta = (n) => (n == null ? '-' : `${n} días`);

// ─── Initial state ───────────────────────────────────────────────────────────
const INITIAL_PO = {
  // Información de la orden
  full_order: '',                  // FK FullOrder (Basado en pedido)
  purchase_order_number: '',       // Orden de compra
  discharge_port: '',              // FK Port
  order_date: '',                  // DD-MM-AAAA
  client_pi: '',                   // PI del cliente
  destination_country: '',         // Lista desplegable
  buyer: '',                       // Comerciante
  shipping_date: '',               // Fecha de envío / CSD

  // Información del cliente
  client: '',                      // FK Client
  rebate: '',                      // Numérico %
  inspection_required: false,      // Boolean (Sí/No)
  payment_terms: '',               // Térmico de pago
  balance_due: '',                 // Saldo pendiente
  pi_issued_by: '',                // PI emitido por
  discount_rate: '',               // Tarifa de descuento %

  // Seguimiento (dropdowns)
  fty_pi_status: '',
  tr_status: '',
  fty_payment: '',
  client_payment: '',
  label_status: '',
  mockup: '',
  quality_control: '',

  // Seguimiento (manual / fechas)
  loading_port: '',
  production_start_date: '',
  production_end_date_real: '',
  theoretical_shipping_date: '',
  actual_shipping_date: '',
  label_delivery_date: '',
  wtb: '',
  invoice_number_uy: '',
  delay_reason_purchases: '',
  delay_responsible_purchases: '',
  delay_reason_kam: '',
  delay_responsible_kam: '',
  order_responsible: '',

  currency: 'USD',
};

const INITIAL_ITEM = {
  // Producto / catálogo
  product: '',
  product_variant: '',
  item_code: '',
  product_image: '',
  customer_item_code: '',
  brand: '',
  description: '',

  // Empaque / dimensiones
  pcs_per_ctn: '',
  inners: '',
  cbm_per_ctn: '',
  fty_pcs_per_ctn: '',
  fty_cbm_per_ctn: '',

  // Cantidades
  order_pcs: '',

  // Identificadores adicionales
  factory_item_code: '',
  factory_po: '',
  upc_ean13: '',
  incoterm: '',

  // Relaciones
  factory: '',
  agent: '',
  factory_port: '',

  // Información del cliente
  customer_battery_info: '',
  battery_fty_info: '',
  color_assortment: '',
  artwork: '',
  comments: '',

  // Costos / precios
  current_cost: '',
  factory_cost: '',
  final_cost: '',
  unit_price: '',
  discount: '',
  target_cost: '',
  customer_extra_unit_price: '',
  extra_charge_customer: '',
  extra_charge_orilat: '',
  operational_cost: '',

  // Totales
  total_cartons: '',
  total_cbm: '',
  total_cost_rmb: '',
  total_fob_cost: '',
  total_fob_price: '',
  total_final_cost_usd: '',
  total_final_cost_rmb: '',
  final_product_fob_price: '',

  // Responsables
  order_responsible: '',
  requested_by: '',
};

// ─── Editor Tabs ─────────────────────────────────────────────────────────────
const EDITOR_TABS = [
  { id: 'orden',        label: 'Información de la orden', icon: Info },
  { id: 'cliente',      label: 'Información del cliente', icon: ClipboardList },
  { id: 'totales',      label: 'Total de la orden',       icon: BarChart3 },
  { id: 'detalle',      label: 'Línea de pedido',         icon: Package },
  { id: 'seguimiento',  label: 'Campos seguimiento',      icon: Truck },
];

// ══════════════════════════════════════════════════════════════════════════════
//  PO EDITOR (full-screen)
// ══════════════════════════════════════════════════════════════════════════════

const POEditor = ({
  po,                 // null/{} → nueva
  contacts = [],
  ports = [],
  fullOrders = [],
  choices = {},
  theme = 'light',
  onBack,
  onSaved,
  onNotify,
}) => {
  const isDark = theme === 'dark';
  const isNew = !po?.id;

  const clientContacts   = useMemo(() => contacts.filter(c => c.contactType === 'CLIENT'),   [contacts]);
  const factoryContacts  = useMemo(() => contacts.filter(c => c.contactType === 'FACTORY'),  [contacts]);
  const supplierContacts = useMemo(() => contacts.filter(c => c.contactType === 'SUPPLIER'), [contacts]);

  const [activeTab, setActiveTab] = useState('orden');
  const [poData, setPoData]       = useState(() => ({ ...INITIAL_PO, ...(po || {}) }));
  const [items, setItems]         = useState([]);
  const [loadingItems, setLoadingItems] = useState(false);
  const [saving, setSaving]       = useState(false);

  const [showItemModal, setShowItemModal] = useState(false);
  const [editingItem, setEditingItem]     = useState(null);
  const [itemForm, setItemForm]           = useState({ ...INITIAL_ITEM });
  const [savingItem, setSavingItem]       = useState(false);
  const [deleteItem, setDeleteItem]       = useState(null);

  const [productSearch, setProductSearch]     = useState('');
  const [productResults, setProductResults]   = useState([]);
  const [searchingProduct, setSearchingProduct] = useState(false);
  const searchProductRef = useRef(null);

  // ── Theme classes ─────────────────────────────────────────────────────────
  const inputCls  = `w-full rounded-lg border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${
    isDark ? 'bg-[#202124] border-[#3c4043] text-[#e8eaed] placeholder-[#9aa0a6]'
           : 'bg-white border-gray-300 text-gray-900 placeholder-gray-400'
  }`;
  const labelCls  = `block text-xs font-medium mb-1 ${isDark ? 'text-[#bdc1c6]' : 'text-gray-600'}`;
  const sText     = isDark ? 'text-[#bdc1c6]' : 'text-gray-600';
  const pText     = isDark ? 'text-[#e8eaed]' : 'text-gray-900';
  const cardCls   = `rounded-xl border shadow-sm ${isDark ? 'bg-[#292a2d] border-[#3c4043]' : 'bg-white border-gray-200'}`;
  const borderCls = isDark ? 'border-[#3c4043]' : 'border-gray-200';

  // ── Load items if editing ─────────────────────────────────────────────────
  useEffect(() => {
    if (!po?.id) return;
    setLoadingItems(true);
    orderService.getPurchaseOrderItems(po.id)
      .then(data => setItems(Array.isArray(data) ? data : data?.results || []))
      .catch(err => {
        console.error('Error cargando items PO:', err);
        setItems([]);
      })
      .finally(() => setLoadingItems(false));
  }, [po?.id]);

  // ── Hidratar form si cambia el PO recibido ────────────────────────────────
  useEffect(() => {
    if (po) {
      const merged = { ...INITIAL_PO };
      Object.keys(INITIAL_PO).forEach(k => {
        if (po[k] !== undefined && po[k] !== null) {
          merged[k] = typeof po[k] === 'boolean' ? po[k] : po[k];
        }
      });
      setPoData(merged);
    }
  }, [po?.id]);

  const set = useCallback((field, value) => {
    setPoData(prev => ({ ...prev, [field]: value }));
  }, []);

  // ── Totals (locales, calculados desde items cargados) ─────────────────────
  const totals = useMemo(() => {
    const order_pcs              = items.reduce((s, it) => s + (parseInt(it.order_pcs) || 0), 0);
    const delivered_pieces       = items.reduce((s, it) => s + (parseInt(it.delivered_pieces) || 0), 0);
    const total_cartons          = items.reduce((s, it) => s + (parseInt(it.total_cartons) || 0), 0);
    const delivered_cartons      = items.reduce((s, it) => s + (parseInt(it.delivered_cartons) || 0), 0);
    const total_cbm              = items.reduce((s, it) => s + (parseFloat(it.total_cbm) || 0), 0);
    const total_fob_cost         = items.reduce((s, it) => s + (parseFloat(it.total_fob_cost) || 0), 0);
    const total_fob_price        = items.reduce((s, it) => s + (parseFloat(it.total_fob_price) || 0), 0);
    const total_cost_rmb         = items.reduce((s, it) => s + (parseFloat(it.total_cost_rmb) || 0), 0);
    const total_final_cost_usd   = items.reduce((s, it) => s + (parseFloat(it.total_final_cost_usd) || 0), 0);
    const total_final_cost_rmb   = items.reduce((s, it) => s + (parseFloat(it.total_final_cost_rmb) || 0), 0);
    const extra_charge_orilat    = items.reduce((s, it) => s + (parseFloat(it.extra_charge_orilat) || 0), 0);
    const extra_charge_customer  = items.reduce((s, it) => s + (parseFloat(it.extra_charge_customer) || 0), 0);
    const margin = total_fob_cost > 0 ? ((total_fob_price / total_fob_cost - 1) * 100) : 0;
    const gross_profit = total_fob_price - total_fob_cost;
    return {
      order_pcs, delivered_pieces, total_cartons, delivered_cartons,
      total_cbm, total_fob_cost, total_fob_price, total_cost_rmb,
      total_final_cost_usd, total_final_cost_rmb,
      extra_charge_orilat, extra_charge_customer,
      margin, gross_profit,
    };
  }, [items]);

  // ── Calculated tracking (replica las @property del backend) ──────────────
  const calc = useMemo(() => {
    const kod = poData.production_start_date;
    const crd = poData.production_end_date_real;
    const atd = poData.actual_shipping_date;
    const csd = poData.shipping_date;

    const addDays = (dateStr, n) => {
      if (!dateStr) return null;
      const d = new Date(dateStr);
      d.setDate(d.getDate() + n);
      return d.toISOString().split('T')[0];
    };

    const crdT = addDays(kod, 45);
    const etdT = crdT ? addDays(crdT, 10) : null;

    return {
      theoretical_production_end_date: crdT,
      theoretical_shipping_date_calc: etdT,
      result_real:        diffDays(atd, csd),
      result_theoretical: etdT ? diffDays(atd, etdT) : null,
      production_days:    diffDays(crd, kod),
      crd_vs_crd_t:       crdT ? diffDays(crd, crdT) : null,
      days_to_lsd:        csd ? diffDays(csd, new Date().toISOString().split('T')[0]) : null,
      full_order_creation_date: po?.full_order_creation_date || null,
    };
  }, [poData.production_start_date, poData.production_end_date_real,
      poData.actual_shipping_date, poData.shipping_date, po?.full_order_creation_date]);

  // ── Save PO ───────────────────────────────────────────────────────────────
  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = { ...poData };

      // Vacíos a null
      Object.keys(payload).forEach(k => {
        if (payload[k] === '') payload[k] = null;
      });

      // FK a int
      ['client', 'full_order', 'discharge_port', 'loading_port'].forEach(k => {
        payload[k] = payload[k] != null ? (parseInt(payload[k], 10) || null) : null;
      });

      // Numéricos
      ['rebate', 'discount_rate', 'balance_due'].forEach(k => {
        payload[k] = payload[k] === null ? null : (parseFloat(payload[k]) || null);
      });

      let saved;
      if (isNew) {
        saved = await orderService.createPurchaseOrder(payload);
      } else {
        saved = await orderService.updatePurchaseOrder(po.id, payload);
      }
      onNotify?.(isNew ? 'PO creada correctamente' : 'PO actualizada correctamente', 'success');
      onSaved?.(saved);
    } catch (err) {
      console.error('Error guardando PO:', err);
      onNotify?.(err?.response?.data?.detail || err?.message || 'Error al guardar PO', 'error');
    } finally {
      setSaving(false);
    }
  };

  // ── Search products for items ─────────────────────────────────────────────
  useEffect(() => {
    if (!productSearch || productSearch.length < 2) { setProductResults([]); return; }
    setSearchingProduct(true);
    clearTimeout(searchProductRef.current);
    searchProductRef.current = setTimeout(async () => {
      try {
        const res = await api.get(`/products/?search=${encodeURIComponent(productSearch)}&limit=15`);
        setProductResults(Array.isArray(res) ? res : res?.results || []);
      } catch { setProductResults([]); }
      finally { setSearchingProduct(false); }
    }, 350);
  }, [productSearch]);

  const applyProduct = (p) => {
    setItemForm(prev => ({
      ...prev,
      product: p.id,
      product_variant: p.name || prev.product_variant,
      item_code: p.product_code || p.code || prev.item_code,
      product_image: p.main_image || p.product_image || prev.product_image,
      brand: (p.brand?.name || p.brand_name) || prev.brand,
      description: p.description || prev.description,
      pcs_per_ctn: p.pcs_per_ctn ?? prev.pcs_per_ctn,
      inners: p.inner_ctn ?? prev.inners,
      cbm_per_ctn: p.cbm_per_ctn ?? prev.cbm_per_ctn,
      incoterm: p.incoterm || prev.incoterm,
      factory_cost: p.cost_fob ?? prev.factory_cost,
      unit_price: p.price_fob ?? prev.unit_price,
      factory_item_code: p.manufacturer_code || prev.factory_item_code,
      factory: p.factory?.id || p.factory || prev.factory,
      agent: p.agent?.id || p.agent || prev.agent,
      factory_port: p.factory_port?.id || p.factory_port || prev.factory_port,
    }));
    setProductSearch('');
    setProductResults([]);
  };

  // ── Item CRUD ─────────────────────────────────────────────────────────────
  const openAddItem = () => {
    setEditingItem(null);
    setItemForm({ ...INITIAL_ITEM });
    setProductSearch('');
    setProductResults([]);
    setShowItemModal(true);
  };

  const openEditItem = (item) => {
    setEditingItem(item);
    const merged = { ...INITIAL_ITEM };
    Object.keys(INITIAL_ITEM).forEach(k => {
      if (item[k] != null) merged[k] = item[k];
    });
    setItemForm(merged);
    setShowItemModal(true);
  };

  const handleSaveItem = async () => {
    if (!po?.id) return;
    setSavingItem(true);
    try {
      const p = { ...itemForm };
      Object.keys(p).forEach(k => { if (p[k] === '') p[k] = null; });

      // FK a int
      ['product', 'factory', 'agent', 'factory_port'].forEach(k => {
        p[k] = p[k] != null ? (parseInt(p[k], 10) || null) : null;
      });

      // Enteros
      ['order_pcs', 'pcs_per_ctn', 'inners', 'fty_pcs_per_ctn', 'total_cartons'].forEach(k => {
        if (p[k] !== null) p[k] = parseInt(p[k], 10);
        if (Number.isNaN(p[k])) p[k] = null;
      });

      // Floats
      [
        'cbm_per_ctn', 'fty_cbm_per_ctn',
        'current_cost', 'factory_cost', 'final_cost', 'unit_price',
        'discount', 'target_cost', 'customer_extra_unit_price',
        'extra_charge_customer', 'extra_charge_orilat', 'operational_cost',
        'total_cbm', 'total_cost_rmb', 'total_fob_cost', 'total_fob_price',
        'total_final_cost_usd', 'total_final_cost_rmb', 'final_product_fob_price',
      ].forEach(k => {
        if (p[k] !== null) p[k] = parseFloat(p[k]);
        if (Number.isNaN(p[k])) p[k] = null;
      });

      if (editingItem) {
        await orderService.updatePurchaseOrderItem(po.id, editingItem.id, p);
      } else {
        await orderService.createPurchaseOrderItem(po.id, p);
      }
      const fresh = await orderService.getPurchaseOrderItems(po.id);
      setItems(Array.isArray(fresh) ? fresh : fresh?.results || []);
      setShowItemModal(false);
      onNotify?.(editingItem ? 'Ítem actualizado' : 'Ítem creado', 'success');
    } catch (err) {
      console.error('Error guardando ítem:', err);
      onNotify?.(err?.response?.data?.detail || err?.message || 'Error al guardar ítem', 'error');
    } finally {
      setSavingItem(false);
    }
  };

  const handleDeleteItem = async (itemId) => {
    if (!po?.id) return;
    try {
      await orderService.deletePurchaseOrderItem(po.id, itemId);
      setItems(prev => prev.filter(it => it.id !== itemId));
      onNotify?.('Ítem eliminado', 'success');
    } catch (err) {
      console.error('Error eliminando ítem:', err);
      onNotify?.('Error al eliminar ítem', 'error');
    }
    setDeleteItem(null);
  };

  const setItem = (field, value) => setItemForm(prev => ({ ...prev, [field]: value }));

  // ── Refs para componentes estables (evitar remounting al tipear) ─────────
  const poDataRef     = useRef(poData);
  const setRef        = useRef(set);
  const inputClsRef   = useRef(inputCls);
  const labelClsRef   = useRef(labelCls);
  const sTextRef      = useRef(sText);
  poDataRef.current   = poData;
  setRef.current      = set;
  inputClsRef.current = inputCls;
  labelClsRef.current = labelCls;
  sTextRef.current    = sText;

  // ── Choice helper (lee `choices` cargado del backend) ─────────────────────
  const choiceOptions = useCallback((field) => {
    const list = choices?.[field] || [];
    return list.map(c => ({
      value: Array.isArray(c) ? c[0] : c.value,
      label: Array.isArray(c) ? c[1] : c.label,
    }));
  }, [choices]);

  const getChoiceLabel = useCallback((field, value) => {
    if (!value) return '-';
    const opt = choiceOptions(field).find(o => o.value === value);
    return opt ? opt.label : value;
  }, [choiceOptions]);

  // ── Render helpers (useMemo para referencias estables) ────────────────────
  const Field = useMemo(() => ({ label, children, span2 = false, hint = null }) => (
    <div className={span2 ? 'sm:col-span-2' : ''}>
      <label className={labelClsRef.current}>{label}</label>
      {children}
      {hint && <p className={`mt-1 text-[11px] ${sTextRef.current}`}>{hint}</p>}
    </div>
  ), []);

  const TextInput = useMemo(() => ({ field, type = 'text', placeholder = '', step }) => (
    <input
      type={type} step={step}
      className={inputClsRef.current}
      value={poDataRef.current[field] ?? ''}
      onChange={e => setRef.current(field, e.target.value)}
      placeholder={placeholder}
    />
  ), []);

  const SelectInput = useMemo(() => ({ field, options = [], placeholder = '— Seleccionar —' }) => (
    <select
      className={inputClsRef.current}
      value={poDataRef.current[field] ?? ''}
      onChange={e => setRef.current(field, e.target.value)}
    >
      <option value="">{placeholder}</option>
      {options.map(o => (
        <option key={String(o.value)} value={o.value}>{o.label}</option>
      ))}
    </select>
  ), []);

  // ──────────────────────────────────────────────────────────────────────────
  //  TAB: Información de la orden
  // ──────────────────────────────────────────────────────────────────────────
  const TabOrden = () => (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
      <Field label="Basado en pedido (Orden Completa)">
        <SelectInput
          field="full_order"
          options={fullOrders.map(fo => ({
            value: fo.id,
            label: `${fo.order_code || `#${fo.id}`} — ${fo.client_name || ''}`,
          }))}
          placeholder="— Sin OC asociada —"
        />
      </Field>
      <Field label="Orden de compra">
        <TextInput field="purchase_order_number" placeholder="Ej: 12345" />
      </Field>
      <Field label="Puerto de descarga">
        <SelectInput
          field="discharge_port"
          options={ports.map(p => ({ value: p.id, label: p.name }))}
        />
      </Field>
      <Field label="Fecha del pedido">
        <TextInput field="order_date" type="date" />
      </Field>
      <Field label="PI del cliente">
        <TextInput field="client_pi" placeholder="Ej: PI-2026-001" />
      </Field>
      <Field label="País de destino">
        <SelectInput
          field="destination_country"
          options={COUNTRY_OPTIONS.map(c => ({ value: c, label: c }))}
        />
      </Field>
      <Field label="Comerciante">
        <TextInput field="buyer" placeholder="Nombre del comerciante / buyer" />
      </Field>
      <Field label="Fecha de envío (CSD)">
        <TextInput field="shipping_date" type="date" />
      </Field>
    </div>
  );

  // ──────────────────────────────────────────────────────────────────────────
  //  TAB: Información del cliente
  // ──────────────────────────────────────────────────────────────────────────
  const TabCliente = () => (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
      <Field label="Cliente *">
        <SelectInput
          field="client"
          options={clientContacts.map(c => ({
            value: c.id,
            label: c.codigoContacto ? `${c.codigoContacto} — ${c.name}` : c.name,
          }))}
          placeholder="Seleccionar cliente..."
        />
      </Field>
      <Field label="Térmico de pago">
        <TextInput field="payment_terms" placeholder="Ej: 30% anticipo, 70% contra BL" />
      </Field>
      <Field label="Rebaja (%)">
        <TextInput field="rebate" type="number" step="0.01" placeholder="0 a 100" />
      </Field>
      <Field label="Saldo pendiente">
        <TextInput field="balance_due" type="number" step="0.01" placeholder="0.00" />
      </Field>
      <Field label="Tarifa de descuento (%)">
        <TextInput field="discount_rate" type="number" step="0.01" placeholder="0.00" />
      </Field>
      <Field label="PI emitido por">
        <TextInput field="pi_issued_by" placeholder="Nombre / entidad emisora" />
      </Field>
      <Field label="Inspección solicitada">
        <select
          className={inputCls}
          value={poData.inspection_required ? 'yes' : 'no'}
          onChange={e => set('inspection_required', e.target.value === 'yes')}
        >
          <option value="no">No</option>
          <option value="yes">Sí</option>
        </select>
      </Field>
    </div>
  );

  // ──────────────────────────────────────────────────────────────────────────
  //  TAB: Total de la orden
  // ──────────────────────────────────────────────────────────────────────────
  const TabTotales = () => {
    const cards = [
      { label: 'Piezas del pedido',                   value: fmtN(totals.order_pcs, 0),                          icon: Hash      },
      { label: 'Costo total del producto (USD)',      value: `USD ${fmtN(totals.total_fob_cost)}`,               icon: DollarSign },
      { label: 'Cargos extras Orilat',                value: `USD ${fmtN(totals.extra_charge_orilat)}`,          icon: Layers     },
      { label: 'Piezas entregadas',                   value: fmtN(totals.delivered_pieces, 0),                   icon: CheckCircle },
      { label: 'Costo total del producto final (USD)',value: `USD ${fmtN(totals.total_final_cost_usd)}`,         icon: DollarSign },
      { label: 'Cargos extras del cliente',           value: `USD ${fmtN(totals.extra_charge_customer)}`,        icon: Layers     },
      { label: 'Total de cartones',                   value: fmtN(totals.total_cartons, 0),                      icon: Package    },
      { label: 'Costo total del producto (RMB)',      value: `RMB ${fmtN(totals.total_cost_rmb)}`,               icon: DollarSign },
      { label: 'Costo total FOB',                     value: `USD ${fmtN(totals.total_fob_cost)}`,               icon: Ship       },
      { label: 'Cartones entregados',                 value: fmtN(totals.delivered_cartons, 0),                  icon: CheckCircle },
      { label: 'Costo total del producto final (RMB)',value: `RMB ${fmtN(totals.total_final_cost_rmb)}`,         icon: DollarSign },
      { label: 'Precio total FOB',                    value: `USD ${fmtN(totals.total_fob_price)}`,              icon: Ship       },
      { label: 'Total CBM',                           value: `${fmtN(totals.total_cbm, 3)} m³`,                  icon: Package    },
      { label: 'Margen',                              value: `${fmtN(totals.margin, 2)} %`,                      icon: BarChart3  },
      { label: 'Ganancia bruta',                      value: `USD ${fmtN(totals.gross_profit)}`,                 icon: DollarSign },
    ];

    return (
      <div className="space-y-5">
        {!po?.id && (
          <div className={`rounded-lg border-l-4 border-blue-500 px-4 py-3 ${isDark ? 'bg-[#1f2937]' : 'bg-blue-50'}`}>
            <p className={`text-sm ${pText}`}>
              Guardá primero la PO para que se calculen los totales a partir de los ítems.
            </p>
          </div>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {cards.map(({ label, value, icon: Icon }, i) => (
            <div key={i} className={`${cardCls} p-4`}>
              <div className="flex items-center gap-2 mb-1">
                <Icon className={`w-4 h-4 ${isDark ? 'text-[#8ab4f8]' : 'text-blue-600'}`} />
                <span className={`text-xs ${sText}`}>{label}</span>
              </div>
              <p className={`text-base font-bold ${pText}`}>{value}</p>
            </div>
          ))}
        </div>
      </div>
    );
  };

  // ──────────────────────────────────────────────────────────────────────────
  //  TAB: Línea de pedido (Detalle)
  // ──────────────────────────────────────────────────────────────────────────
  const TabDetalle = () => {
    const isExisting = !!po?.id;

    const cols = [
      { key: 'product_image',         label: 'Imagen' },
      { key: 'item_code',             label: 'Cód. Artículo' },
      { key: 'product_variant',       label: 'Variante' },
      { key: 'customer_item_code',    label: 'Cód. Artículo Cliente' },
      { key: 'brand',                 label: 'Marca' },
      { key: 'factory_name',          label: 'Fábrica' },
      { key: 'factory_item_code',     label: 'Cód. Fábrica' },
      { key: 'factory_po',            label: 'Orden Fábrica' },
      { key: 'order_pcs',             label: 'Piezas' },
      { key: 'pcs_per_ctn',           label: 'PCS/CTN' },
      { key: 'cbm_per_ctn',           label: 'CBM/CTN' },
      { key: 'total_cartons',         label: 'Total Cartones' },
      { key: 'total_cbm',             label: 'Total CBM' },
      { key: 'unit_price',            label: 'Precio Unit.' },
      { key: 'final_cost',            label: 'Costo Final' },
      { key: 'final_product_fob_price', label: 'Precio Final FOB' },
      { key: 'total_fob_cost',        label: 'Costo Total USD' },
      { key: 'total_cost_rmb',        label: 'Costo Total RMB' },
      { key: 'order_responsible',     label: 'Responsable' },
    ];

    const renderCell = (item, key) => {
      if (key === 'product_image') {
        return item.product_image
          ? <img src={item.product_image} alt="" className="w-10 h-10 object-cover rounded-md" />
          : <div className="w-10 h-10 bg-gray-100 rounded-md flex items-center justify-center"><Package size={14} className="text-gray-400" /></div>;
      }
      const numKeys = ['unit_price', 'final_cost', 'final_product_fob_price', 'total_fob_cost', 'total_cost_rmb', 'cbm_per_ctn', 'total_cbm'];
      const intKeys = ['order_pcs', 'pcs_per_ctn', 'total_cartons'];
      const v = item[key];
      if (v == null || v === '') return <span className="text-gray-400">—</span>;
      if (numKeys.includes(key)) return fmtN(v);
      if (intKeys.includes(key)) return fmtN(v, 0);
      return String(v);
    };

    if (!isExisting) {
      return (
        <div className="flex flex-col items-center justify-center py-16 text-center">
          <Package size={48} className="mb-3 text-gray-300" />
          <p className={`text-sm ${sText} max-w-xs`}>
            Guardá primero la PO para poder agregar líneas al detalle.
          </p>
        </div>
      );
    }

    return (
      <div>
        <div className="flex items-center justify-between mb-4">
          <p className={`text-sm ${sText}`}>{items.length} línea{items.length !== 1 ? 's' : ''}</p>
          <button
            onClick={openAddItem}
            className="flex items-center gap-2 px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium"
          >
            <Plus size={15} /> Agregar Ítem
          </button>
        </div>

        {loadingItems ? (
          <div className="flex items-center justify-center py-12">
            <RefreshCw className="w-6 h-6 animate-spin text-blue-500" />
          </div>
        ) : items.length === 0 ? (
          <div className={`flex flex-col items-center justify-center py-16 border border-dashed rounded-xl ${borderCls}`}>
            <Package size={40} className="mb-3 text-gray-300" />
            <p className={`text-sm mb-3 ${sText}`}>No hay ítems en esta PO</p>
            <button onClick={openAddItem} className="text-sm text-blue-600 hover:underline font-medium">
              Agregar el primer ítem
            </button>
          </div>
        ) : (
          <div className={`overflow-x-auto rounded-xl border ${borderCls}`}>
            <table className="w-full text-sm">
              <thead>
                <tr className={`${isDark ? 'bg-[#35363a]' : 'bg-gray-50'} border-b ${borderCls}`}>
                  {cols.map(c => (
                    <th key={c.key} className={`px-3 py-3 text-left text-xs font-semibold uppercase tracking-wide whitespace-nowrap ${sText}`}>
                      {c.label}
                    </th>
                  ))}
                  <th className={`px-3 py-3 text-left text-xs font-semibold uppercase tracking-wide ${sText}`}>Acciones</th>
                </tr>
              </thead>
              <tbody className={`divide-y ${isDark ? 'divide-[#3c4043]' : 'divide-gray-100'}`}>
                {items.map((item, idx) => (
                  <tr key={item.id || idx} className={isDark ? 'hover:bg-[#35363a]' : (idx % 2 === 0 ? 'bg-white' : 'bg-gray-50/50')}>
                    {cols.map(c => (
                      <td key={c.key} className={`px-3 py-2.5 ${c.key === 'product_variant' ? 'font-medium' : ''} whitespace-nowrap ${pText}`}>
                        {renderCell(item, c.key)}
                      </td>
                    ))}
                    <td className="px-3 py-2.5">
                      <div className="flex items-center gap-1">
                        <button onClick={() => openEditItem(item)} className="p-1.5 rounded-lg hover:bg-blue-50 transition-colors" title="Editar">
                          <Edit size={13} className="text-blue-500" />
                        </button>
                        <button onClick={() => setDeleteItem(item)} className="p-1.5 rounded-lg hover:bg-red-50 transition-colors" title="Eliminar">
                          <Trash2 size={13} className="text-red-500" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    );
  };

  // ──────────────────────────────────────────────────────────────────────────
  //  TAB: Campos seguimiento
  // ──────────────────────────────────────────────────────────────────────────
  const TabSeguimiento = () => {
    const calcCard = (label, value, Icon) => (
      <div className={`rounded-lg p-3 border ${isDark ? 'bg-[#202124] border-[#3c4043]' : 'bg-gray-50 border-gray-100'}`}>
        <div className="flex items-center gap-2 mb-1">
          <Icon className={`w-3.5 h-3.5 ${isDark ? 'text-[#8ab4f8]' : 'text-blue-600'}`} />
          <span className={`text-xs ${sText}`}>{label}</span>
        </div>
        <p className={`text-sm font-bold ${pText}`}>{value}</p>
      </div>
    );

    return (
      <div className="space-y-6">
        {/* Dropdowns de seguimiento */}
        <section>
          <h3 className={`text-sm font-semibold mb-3 ${pText}`}>Estados del proceso</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            <Field label="Status PI fábrica - FTY PI Status">
              <SelectInput field="fty_pi_status" options={choiceOptions('fty_pi_status')} />
            </Field>
            <Field label="TR Status">
              <SelectInput field="tr_status" options={choiceOptions('tr_status')} />
            </Field>
            <Field label="FTY Payment">
              <SelectInput field="fty_payment" options={choiceOptions('fty_payment')} />
            </Field>
            <Field label="Client Payment for production / shipping">
              <SelectInput field="client_payment" options={choiceOptions('client_payment')} />
            </Field>
            <Field label="Estado de etiquetas">
              <SelectInput field="label_status" options={choiceOptions('label_status')} />
            </Field>
            <Field label="Control de calidad - Production QC">
              <SelectInput field="quality_control" options={choiceOptions('quality_control')} />
            </Field>
            <Field label="Mock-up">
              <SelectInput field="mockup" options={choiceOptions('mockup')} />
            </Field>
            <Field label="Puerto de carga">
              <SelectInput field="loading_port" options={ports.map(p => ({ value: p.id, label: p.name }))} />
            </Field>
          </div>
        </section>

        {/* Fechas */}
        <section>
          <h3 className={`text-sm font-semibold mb-3 ${pText}`}>Fechas de seguimiento</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            <Field label="Fecha de inicio de la producción (KOD)">
              <TextInput field="production_start_date" type="date" />
            </Field>
            <Field label="Fecha real de fin de producción (CRD)">
              <TextInput field="production_end_date_real" type="date" />
            </Field>
            <Field label="Fecha teórica de embarque (manual)">
              <TextInput field="theoretical_shipping_date" type="date" />
            </Field>
            <Field label="Fecha real de embarque (ATD)">
              <TextInput field="actual_shipping_date" type="date" />
            </Field>
            <Field label="Fecha entrega de etiquetas (ESD)">
              <TextInput field="label_delivery_date" type="date" />
            </Field>
            <Field label="Fecha estimada de embarque - CSD">
              <TextInput field="shipping_date" type="date" />
            </Field>
          </div>
        </section>

        {/* Campos manuales */}
        <section>
          <h3 className={`text-sm font-semibold mb-3 ${pText}`}>Campos manuales</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            <Field label="WTB">
              <TextInput field="wtb" />
            </Field>
            <Field label="N° Factura UY">
              <TextInput field="invoice_number_uy" />
            </Field>
            <Field label="Responsable OC">
              <TextInput field="order_responsible" />
            </Field>
            <Field label="Motivo de error o demora (Compras)" span2>
              <textarea
                rows={2} className={inputCls}
                value={poData.delay_reason_purchases || ''}
                onChange={e => set('delay_reason_purchases', e.target.value)}
              />
            </Field>
            <Field label="Responsable del error o demora (Compras)">
              <TextInput field="delay_responsible_purchases" />
            </Field>
            <Field label="Motivo de error o demora (KAM)" span2>
              <textarea
                rows={2} className={inputCls}
                value={poData.delay_reason_kam || ''}
                onChange={e => set('delay_reason_kam', e.target.value)}
              />
            </Field>
            <Field label="Responsable del error o demora (KAM)">
              <TextInput field="delay_responsible_kam" />
            </Field>
          </div>
        </section>

        {/* Campos calculados (read-only) */}
        <section>
          <h3 className={`text-sm font-semibold mb-3 ${pText}`}>Campos calculados</h3>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
            {calcCard('Fecha teórica fin producción CRD(T) = KOD + 45d', fmtDate(calc.theoretical_production_end_date), Calendar)}
            {calcCard('Fecha teórica embarque ETD(T) = CRD(T) + 10d',     fmtDate(calc.theoretical_shipping_date_calc),  Ship)}
            {calcCard('Resultado Real (ATD - CSD)',                       formatDayDelta(calc.result_real),              AlertCircle)}
            {calcCard('Resultado Teórico (ATD - ETD(T))',                 formatDayDelta(calc.result_theoretical),       BarChart3)}
            {calcCard('Días de producción (CRD - KOD)',                   formatDayDelta(calc.production_days),          Clock)}
            {calcCard('CRD vs CRD(T)',                                    formatDayDelta(calc.crd_vs_crd_t),             AlertTriangle)}
            {calcCard('DAYS TO LSD / Booking Status',                     formatDayDelta(calc.days_to_lsd),              Clock)}
            {calcCard('Fecha creación Full Order',                        fmtDate(calc.full_order_creation_date),        FileText)}
          </div>
        </section>
      </div>
    );
  };

  // ──────────────────────────────────────────────────────────────────────────
  //  ITEM MODAL
  // ──────────────────────────────────────────────────────────────────────────
  const ItemModal = () => (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className={`w-full max-w-4xl rounded-xl shadow-2xl border max-h-[90vh] flex flex-col ${cardCls}`}>
        <div className={`flex items-center justify-between p-5 border-b ${borderCls}`}>
          <h3 className={`text-lg font-semibold ${pText}`}>{editingItem ? 'Editar Línea' : 'Agregar Línea'}</h3>
          <button onClick={() => setShowItemModal(false)} className={`p-1 rounded-lg ${isDark ? 'hover:bg-[#35363a]' : 'hover:bg-gray-100'}`}>
            <X size={20} className={sText} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-5 space-y-5">
          {/* Búsqueda producto */}
          <div>
            <label className={labelCls}>Buscar producto del catálogo</label>
            <div className="relative">
              <Search size={14} className={`absolute left-3 top-1/2 -translate-y-1/2 ${sText}`} />
              <input
                className={`${inputCls} pl-8`}
                value={productSearch}
                onChange={e => setProductSearch(e.target.value)}
                placeholder="Buscar por nombre o código..."
              />
              {searchingProduct && <div className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />}
            </div>
            {productResults.length > 0 && (
              <div className={`mt-1 border rounded-lg shadow-lg max-h-40 overflow-y-auto ${isDark ? 'bg-[#202124] border-[#3c4043]' : 'bg-white border-gray-200'}`}>
                {productResults.map(p => (
                  <button
                    key={p.id}
                    onClick={() => applyProduct(p)}
                    className={`w-full text-left px-3 py-2 text-sm flex items-center gap-2 border-b last:border-0 ${isDark ? 'hover:bg-[#35363a] border-[#3c4043]' : 'hover:bg-blue-50 border-gray-100'}`}
                  >
                    {p.main_image && <img src={p.main_image} alt="" className="w-8 h-8 object-cover rounded" />}
                    <div>
                      <div className={`font-medium ${pText}`}>{p.name}</div>
                      <div className={`text-xs ${sText}`}>{p.product_code || p.code || ''}</div>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Producto */}
          <fieldset className="space-y-3">
            <legend className={`text-xs font-semibold uppercase tracking-wide ${sText}`}>Producto</legend>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Field label="Variante del Producto">
                <input className={inputCls} value={itemForm.product_variant} onChange={e => setItem('product_variant', e.target.value)} />
              </Field>
              <Field label="Código del Artículo">
                <input className={inputCls} value={itemForm.item_code} onChange={e => setItem('item_code', e.target.value)} />
              </Field>
              <Field label="Imagen del Producto (URL)">
                <input className={inputCls} value={itemForm.product_image} onChange={e => setItem('product_image', e.target.value)} placeholder="https://..." />
              </Field>
              <Field label="Código del Artículo del Cliente">
                <input className={inputCls} value={itemForm.customer_item_code} onChange={e => setItem('customer_item_code', e.target.value)} />
              </Field>
              <Field label="Marca">
                <input className={inputCls} value={itemForm.brand} onChange={e => setItem('brand', e.target.value)} />
              </Field>
              <Field label="UPC / EA">
                <input className={inputCls} value={itemForm.upc_ean13} onChange={e => setItem('upc_ean13', e.target.value)} />
              </Field>
              <Field label="Descripción" span2>
                <textarea rows={2} className={inputCls} value={itemForm.description} onChange={e => setItem('description', e.target.value)} />
              </Field>
            </div>
          </fieldset>

          {/* Empaque y dimensiones */}
          <fieldset className="space-y-3">
            <legend className={`text-xs font-semibold uppercase tracking-wide ${sText}`}>Empaque y dimensiones</legend>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <Field label="PCS/CTN">
                <input type="number" className={inputCls} value={itemForm.pcs_per_ctn} onChange={e => setItem('pcs_per_ctn', e.target.value)} />
              </Field>
              <Field label="Internos por Cartón Maestro">
                <input type="number" className={inputCls} value={itemForm.inners} onChange={e => setItem('inners', e.target.value)} />
              </Field>
              <Field label="CBM/CTN">
                <input type="number" step="0.001" className={inputCls} value={itemForm.cbm_per_ctn} onChange={e => setItem('cbm_per_ctn', e.target.value)} />
              </Field>
              <Field label="FTY PCS/CTN">
                <input type="number" className={inputCls} value={itemForm.fty_pcs_per_ctn} onChange={e => setItem('fty_pcs_per_ctn', e.target.value)} />
              </Field>
              <Field label="FTY CBM/CTN">
                <input type="number" step="0.001" className={inputCls} value={itemForm.fty_cbm_per_ctn} onChange={e => setItem('fty_cbm_per_ctn', e.target.value)} />
              </Field>
              <Field label="Piezas del Pedido">
                <input type="number" className={inputCls} value={itemForm.order_pcs} onChange={e => setItem('order_pcs', e.target.value)} />
              </Field>
              <Field label="Total Cartones">
                <input type="number" className={inputCls} value={itemForm.total_cartons} onChange={e => setItem('total_cartons', e.target.value)} />
              </Field>
              <Field label="Total CBM">
                <input type="number" step="0.001" className={inputCls} value={itemForm.total_cbm} onChange={e => setItem('total_cbm', e.target.value)} />
              </Field>
            </div>
          </fieldset>

          {/* Fábrica */}
          <fieldset className="space-y-3">
            <legend className={`text-xs font-semibold uppercase tracking-wide ${sText}`}>Fábrica</legend>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Field label="Fábrica">
                <select className={inputCls} value={itemForm.factory || ''} onChange={e => setItem('factory', e.target.value)}>
                  <option value="">— Seleccionar —</option>
                  {factoryContacts.map(f => (
                    <option key={f.id} value={f.id}>{f.name}</option>
                  ))}
                </select>
              </Field>
              <Field label="Proveedor / Agente">
                <select className={inputCls} value={itemForm.agent || ''} onChange={e => setItem('agent', e.target.value)}>
                  <option value="">— Seleccionar —</option>
                  {supplierContacts.map(s => (
                    <option key={s.id} value={s.id}>{s.name}</option>
                  ))}
                </select>
              </Field>
              <Field label="Código del Artículo de la Fábrica">
                <input className={inputCls} value={itemForm.factory_item_code} onChange={e => setItem('factory_item_code', e.target.value)} />
              </Field>
              <Field label="Orden de la Fábrica">
                <input className={inputCls} value={itemForm.factory_po} onChange={e => setItem('factory_po', e.target.value)} />
              </Field>
              <Field label="Puerto de la Fábrica">
                <select className={inputCls} value={itemForm.factory_port || ''} onChange={e => setItem('factory_port', e.target.value)}>
                  <option value="">— Seleccionar —</option>
                  {ports.map(p => (
                    <option key={p.id} value={p.id}>{p.name}</option>
                  ))}
                </select>
              </Field>
              <Field label="Incoterm">
                <input className={inputCls} value={itemForm.incoterm} onChange={e => setItem('incoterm', e.target.value)} placeholder="Ej: FOB, EXW" />
              </Field>
            </div>
          </fieldset>

          {/* Información del cliente */}
          <fieldset className="space-y-3">
            <legend className={`text-xs font-semibold uppercase tracking-wide ${sText}`}>Información del cliente</legend>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Field label="Información de la Batería del Cliente">
                <input className={inputCls} value={itemForm.customer_battery_info} onChange={e => setItem('customer_battery_info', e.target.value)} />
              </Field>
              <Field label="Battery FTY Info">
                <input className={inputCls} value={itemForm.battery_fty_info} onChange={e => setItem('battery_fty_info', e.target.value)} />
              </Field>
              <Field label="Color Assortment">
                <input className={inputCls} value={itemForm.color_assortment} onChange={e => setItem('color_assortment', e.target.value)} />
              </Field>
              <Field label="Artwork" span2>
                <textarea rows={2} className={inputCls} value={itemForm.artwork} onChange={e => setItem('artwork', e.target.value)} />
              </Field>
              <Field label="Comentarios" span2>
                <textarea rows={2} className={inputCls} value={itemForm.comments} onChange={e => setItem('comments', e.target.value)} />
              </Field>
            </div>
          </fieldset>

          {/* Costos y precios */}
          <fieldset className="space-y-3">
            <legend className={`text-xs font-semibold uppercase tracking-wide ${sText}`}>Costos y precios</legend>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              <Field label="Costo Actual">
                <input type="number" step="0.000001" className={inputCls} value={itemForm.current_cost} onChange={e => setItem('current_cost', e.target.value)} />
              </Field>
              <Field label="Costo FTY">
                <input type="number" step="0.000001" className={inputCls} value={itemForm.factory_cost} onChange={e => setItem('factory_cost', e.target.value)} />
              </Field>
              <Field label="Costo Final">
                <input type="number" step="0.000001" className={inputCls} value={itemForm.final_cost} onChange={e => setItem('final_cost', e.target.value)} />
              </Field>
              <Field label="Precio Unitario">
                <input type="number" step="0.000001" className={inputCls} value={itemForm.unit_price} onChange={e => setItem('unit_price', e.target.value)} />
              </Field>
              <Field label="Descuento (%)">
                <input type="number" step="0.01" className={inputCls} value={itemForm.discount} onChange={e => setItem('discount', e.target.value)} />
              </Field>
              <Field label="Costo Objetivo">
                <input type="number" step="0.000001" className={inputCls} value={itemForm.target_cost} onChange={e => setItem('target_cost', e.target.value)} />
              </Field>
              <Field label="Precio Unitario del Cliente Extra">
                <input type="number" step="0.000001" className={inputCls} value={itemForm.customer_extra_unit_price} onChange={e => setItem('customer_extra_unit_price', e.target.value)} />
              </Field>
              <Field label="Carga Extra del Cliente">
                <input type="number" step="0.01" className={inputCls} value={itemForm.extra_charge_customer} onChange={e => setItem('extra_charge_customer', e.target.value)} />
              </Field>
              <Field label="Carga Extra de Orilat">
                <input type="number" step="0.01" className={inputCls} value={itemForm.extra_charge_orilat} onChange={e => setItem('extra_charge_orilat', e.target.value)} />
              </Field>
              <Field label="Costo Operativo">
                <input type="number" step="0.01" className={inputCls} value={itemForm.operational_cost} onChange={e => setItem('operational_cost', e.target.value)} />
              </Field>
              <Field label="Precio Final FOB">
                <input type="number" step="0.000001" className={inputCls} value={itemForm.final_product_fob_price} onChange={e => setItem('final_product_fob_price', e.target.value)} />
              </Field>
            </div>
          </fieldset>

          {/* Totales línea */}
          <fieldset className="space-y-3">
            <legend className={`text-xs font-semibold uppercase tracking-wide ${sText}`}>Totales de línea</legend>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              <Field label="Costo Total USD">
                <input type="number" step="0.01" className={inputCls} value={itemForm.total_fob_cost} onChange={e => setItem('total_fob_cost', e.target.value)} />
              </Field>
              <Field label="Costo Total RMB">
                <input type="number" step="0.01" className={inputCls} value={itemForm.total_cost_rmb} onChange={e => setItem('total_cost_rmb', e.target.value)} />
              </Field>
              <Field label="Precio Total FOB">
                <input type="number" step="0.01" className={inputCls} value={itemForm.total_fob_price} onChange={e => setItem('total_fob_price', e.target.value)} />
              </Field>
              <Field label="Costo Total Final USD">
                <input type="number" step="0.01" className={inputCls} value={itemForm.total_final_cost_usd} onChange={e => setItem('total_final_cost_usd', e.target.value)} />
              </Field>
              <Field label="Costo Total Final RMB">
                <input type="number" step="0.01" className={inputCls} value={itemForm.total_final_cost_rmb} onChange={e => setItem('total_final_cost_rmb', e.target.value)} />
              </Field>
            </div>
          </fieldset>

          {/* Responsables */}
          <fieldset className="space-y-3">
            <legend className={`text-xs font-semibold uppercase tracking-wide ${sText}`}>Responsables</legend>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Field label="Responsable del Pedido">
                <input className={inputCls} value={itemForm.order_responsible} onChange={e => setItem('order_responsible', e.target.value)} />
              </Field>
              <Field label="Solicitado por">
                <input className={inputCls} value={itemForm.requested_by} onChange={e => setItem('requested_by', e.target.value)} />
              </Field>
            </div>
          </fieldset>
        </div>

        <div className={`flex justify-end gap-3 p-5 border-t ${borderCls}`}>
          <button onClick={() => setShowItemModal(false)} className={`px-4 py-2 rounded-lg text-sm font-medium ${isDark ? 'bg-[#35363a] text-[#e8eaed] hover:bg-[#3c4043]' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}>
            Cancelar
          </button>
          <button onClick={handleSaveItem} disabled={savingItem} className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium disabled:opacity-50">
            <Save size={15} />
            {savingItem ? 'Guardando...' : 'Guardar Línea'}
          </button>
        </div>
      </div>
    </div>
  );

  // ──────────────────────────────────────────────────────────────────────────
  //  Main render del editor
  // ──────────────────────────────────────────────────────────────────────────
  return (
    <>
      <div className={cardCls}>
        {/* Header */}
        <div className={`flex items-center justify-between px-6 py-4 border-b ${borderCls}`}>
          <div className="flex items-center gap-3">
            <button
              onClick={onBack}
              className={`p-2 rounded-lg transition-colors ${isDark ? 'hover:bg-[#35363a]' : 'hover:bg-gray-100'}`}
            >
              <ChevronLeft size={18} className={sText} />
            </button>
            <div>
              <h2 className={`text-lg font-bold ${pText}`}>
                {isNew ? 'Nueva PO Individual' : (po.po_code || `PO #${po.id}`)}
              </h2>
              {!isNew && po.client_name && (
                <p className={`text-xs ${sText}`}>{po.client_name}</p>
              )}
            </div>
            {!isNew && po.fty_pi_status && (() => {
              const c = FTY_PI_STATUS_COLORS[po.fty_pi_status] || { bg: 'bg-gray-100 text-gray-700 border-gray-300', dot: 'bg-gray-400' };
              return (
                <span className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium border ${c.bg}`}>
                  <span className={`w-1.5 h-1.5 rounded-full ${c.dot}`} />
                  {getChoiceLabel('fty_pi_status', po.fty_pi_status)}
                </span>
              );
            })()}
          </div>
          <button
            onClick={handleSave}
            disabled={saving}
            className="flex items-center gap-2 px-5 py-1.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-medium disabled:opacity-50"
          >
            <Save size={15} />
            {saving ? 'Guardando...' : 'Guardar'}
          </button>
        </div>

        {/* Tabs */}
        <div className={`flex gap-1 px-6 pt-3 border-b overflow-x-auto ${borderCls}`}>
          {EDITOR_TABS.map(tab => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-1.5 px-4 py-2 text-sm font-medium transition-all border-b-2 -mb-px whitespace-nowrap ${
                  activeTab === tab.id
                    ? 'border-blue-600 text-blue-600'
                    : `border-transparent ${sText} hover:text-blue-500 hover:border-gray-300`
                }`}
              >
                <Icon className="w-4 h-4" /> {tab.label}
              </button>
            );
          })}
        </div>

        {/* Tab content */}
        <div className="p-6">
          {activeTab === 'orden'        && TabOrden()}
          {activeTab === 'cliente'      && TabCliente()}
          {activeTab === 'totales'      && TabTotales()}
          {activeTab === 'detalle'      && TabDetalle()}
          {activeTab === 'seguimiento'  && TabSeguimiento()}
        </div>
      </div>

      {showItemModal && ItemModal()}

      {deleteItem && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className={`${cardCls} p-6 max-w-sm w-full text-center`}>
            <div className="mx-auto w-12 h-12 rounded-full bg-red-100 flex items-center justify-center mb-4">
              <AlertCircle size={24} className="text-red-600" />
            </div>
            <h3 className={`text-lg font-semibold mb-2 ${pText}`}>Eliminar línea</h3>
            <p className={`text-sm mb-6 ${sText}`}>
              ¿Eliminar <strong>{deleteItem.product_variant || deleteItem.item_code || 'esta línea'}</strong>?
            </p>
            <div className="flex justify-center gap-3">
              <button onClick={() => setDeleteItem(null)} className={`px-4 py-2 rounded-lg text-sm font-medium ${isDark ? 'bg-[#35363a] text-[#e8eaed] hover:bg-[#3c4043]' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}>
                Cancelar
              </button>
              <button onClick={() => handleDeleteItem(deleteItem.id)} className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 text-sm font-medium">
                <Trash2 size={15} /> Eliminar
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

// ══════════════════════════════════════════════════════════════════════════════
//  MAIN COMPONENT (LIST + EDITOR ROUTER)
// ══════════════════════════════════════════════════════════════════════════════

const PurchaseOrderManager = ({ theme = 'light', contacts = [] }) => {
  const isDark = theme === 'dark';

  const [orders, setOrders]       = useState([]);
  const [loading, setLoading]     = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [choices, setChoices]     = useState({});
  const [ports, setPorts]         = useState([]);
  const [fullOrders, setFullOrders] = useState([]);
  const [notification, setNotification] = useState(null);

  const [activePO, setActivePO]   = useState(null); // null → list, object/{} → editor
  const [deleteTarget, setDeleteTarget] = useState(null);

  // ── Theme classes ─────────────────────────────────────────────────────────
  const sText     = isDark ? 'text-[#bdc1c6]' : 'text-gray-600';
  const pText     = isDark ? 'text-[#e8eaed]' : 'text-gray-900';
  const cardCls   = `rounded-xl border shadow-sm ${isDark ? 'bg-[#292a2d] border-[#3c4043]' : 'bg-white border-gray-200'}`;
  const inputCls  = `w-full rounded-lg border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${isDark ? 'bg-[#202124] border-[#3c4043] text-[#e8eaed] placeholder-[#9aa0a6]' : 'bg-white border-gray-300 text-gray-900 placeholder-gray-400'}`;
  const borderCls = isDark ? 'border-[#3c4043]' : 'border-gray-200';

  const showNotification = useCallback((message, type = 'info') => {
    setNotification({ message, type, duration: 4000, id: Date.now() });
  }, []);

  // ── Loaders ───────────────────────────────────────────────────────────────
  const loadOrders = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (searchTerm) params.search = searchTerm;
      if (statusFilter !== 'all') params.fty_pi_status = statusFilter;
      const data = await orderService.getPurchaseOrders(params);
      setOrders(Array.isArray(data) ? data : data?.results || []);
    } catch (err) {
      console.error('Error cargando POs:', err);
      showNotification('Error al cargar PO Individuales', 'error');
    } finally {
      setLoading(false);
    }
  }, [searchTerm, statusFilter, showNotification]);

  const loadChoices = useCallback(async () => {
    try {
      const data = await orderService.getChoices();
      setChoices(data || {});
    } catch (err) {
      console.error('Error cargando choices:', err);
    }
  }, []);

  useEffect(() => { loadOrders(); }, [loadOrders]);
  useEffect(() => { loadChoices(); }, [loadChoices]);

  useEffect(() => {
    api.get('/pricing/ports/')
      .then(data => setPorts(Array.isArray(data) ? data : data?.results || []))
      .catch(err => console.error('Error cargando puertos:', err));
  }, []);

  useEffect(() => {
    orderService.getFullOrders()
      .then(data => setFullOrders(Array.isArray(data) ? data : data?.results || []))
      .catch(err => console.error('Error cargando Full Orders:', err));
  }, []);

  // ── Open / save / delete ──────────────────────────────────────────────────
  const openPO = useCallback(async (poId) => {
    try {
      const detail = await orderService.getPurchaseOrder(poId);
      setActivePO(detail);
    } catch (err) {
      console.error('Error abriendo PO:', err);
      showNotification('Error al abrir la PO', 'error');
    }
  }, [showNotification]);

  const openNew = useCallback(() => setActivePO({}), []);

  const handleBack = useCallback(() => {
    setActivePO(null);
    loadOrders();
  }, [loadOrders]);

  const handleSaved = useCallback(async (saved) => {
    await loadOrders();
    if (saved?.id) {
      const detail = await orderService.getPurchaseOrder(saved.id);
      setActivePO(detail);
    }
  }, [loadOrders]);

  const handleDelete = useCallback(async () => {
    if (!deleteTarget) return;
    try {
      await orderService.deletePurchaseOrder(deleteTarget.id);
      if (activePO?.id === deleteTarget.id) setActivePO(null);
      setDeleteTarget(null);
      await loadOrders();
      showNotification('PO eliminada', 'success');
    } catch (err) {
      console.error('Error eliminando PO:', err);
      showNotification('Error al eliminar PO', 'error');
    }
  }, [deleteTarget, activePO, loadOrders, showNotification]);

  // ── Stats / filtering ─────────────────────────────────────────────────────
  const stats = useMemo(() => {
    const total = orders.length;
    const byStatus = {};
    let totalFob = 0;
    orders.forEach(o => {
      const st = o.fty_pi_status || 'sin_estado';
      byStatus[st] = (byStatus[st] || 0) + 1;
      totalFob += parseFloat(o.total_fob_price || 0);
    });
    return { total, byStatus, totalFob };
  }, [orders]);

  const filteredOrders = useMemo(() => orders.filter(o => {
    if (statusFilter !== 'all' && o.fty_pi_status !== statusFilter) return false;
    if (searchTerm) {
      const q = searchTerm.toLowerCase();
      return [o.po_code, o.client_name, o.buyer, o.purchase_order_number, o.client_pi]
        .filter(Boolean).join(' ').toLowerCase().includes(q);
    }
    return true;
  }), [orders, statusFilter, searchTerm]);

  const statusOptions = useMemo(() => {
    const list = choices.fty_pi_status || [];
    return list.map(c => ({
      value: Array.isArray(c) ? c[0] : c.value,
      label: Array.isArray(c) ? c[1] : c.label,
    }));
  }, [choices]);

  const getChoiceLabel = useCallback((field, value) => {
    if (!value) return '-';
    const list = choices?.[field] || [];
    const found = list.find(c => (Array.isArray(c) ? c[0] : c.value) === value);
    if (!found) return value;
    return Array.isArray(found) ? found[1] : found.label;
  }, [choices]);

  // ──────────────────────────────────────────────────────────────────────────
  //  EDITOR view
  // ──────────────────────────────────────────────────────────────────────────
  if (activePO !== null) {
    return (
      <>
        <AnimatePresence>
          {notification && (
            <Notification
              message={notification.message}
              type={notification.type}
              duration={notification.duration}
              onClose={() => setNotification(null)}
            />
          )}
        </AnimatePresence>
        <POEditor
          po={activePO}
          contacts={contacts}
          ports={ports}
          fullOrders={fullOrders}
          choices={choices}
          theme={theme}
          onBack={handleBack}
          onSaved={handleSaved}
          onNotify={showNotification}
        />
      </>
    );
  }

  // ──────────────────────────────────────────────────────────────────────────
  //  LIST view
  // ──────────────────────────────────────────────────────────────────────────
  return (
    <div className={isDark ? 'text-[#e8eaed]' : 'text-gray-900'}>
      <AnimatePresence>
        {notification && (
          <Notification
            message={notification.message}
            type={notification.type}
            duration={notification.duration}
            onClose={() => setNotification(null)}
          />
        )}
      </AnimatePresence>

      <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <Truck className="w-6 h-6 text-blue-500" />
              PO Individual
            </h1>
            <p className={`text-sm mt-1 ${sText}`}>
              Gestión de órdenes de compra individuales
            </p>
          </div>
          <button
            onClick={openNew}
            className="flex items-center gap-2 px-4 py-2.5 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition-colors shadow-sm text-sm font-medium"
          >
            <Plus className="w-4 h-4" /> Nueva PO
          </button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
          {[
            { label: 'Total POs',    value: stats.total,                                                                       icon: ClipboardList, color: 'blue'   },
            { label: 'Confirmadas',  value: stats.byStatus.confirmed || 0,                                                     icon: CheckCircle,   color: 'green'  },
            { label: 'En proceso',   value: (stats.byStatus.po_in_process || 0) + (stats.byStatus.po_sent || 0),               icon: Clock,         color: 'sky'    },
            { label: 'Total FOB',    value: `USD ${fmtN(stats.totalFob)}`,                                                     icon: DollarSign,    color: 'purple' },
          ].map(({ label, value, icon: Icon, color }, i) => (
            <div key={i} className={`${cardCls} p-4`}>
              <div className="flex items-center gap-3">
                <div className={`p-2 rounded-lg bg-${color}-100`}>
                  <Icon className={`w-5 h-5 text-${color}-600`} />
                </div>
                <div>
                  <p className={`text-xs ${sText}`}>{label}</p>
                  <p className={`text-lg font-bold ${pText}`}>{value}</p>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Search + filters */}
        <div className={`${cardCls} p-4 mb-6`}>
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="relative flex-1">
              <Search className={`absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 ${sText}`} />
              <input
                type="text"
                placeholder="Buscar por código PO, cliente, comerciante, OC..."
                className={`${inputCls} pl-10`}
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
              />
              {searchTerm && (
                <button onClick={() => setSearchTerm('')} className="absolute right-3 top-1/2 -translate-y-1/2">
                  <X className={`w-4 h-4 ${sText}`} />
                </button>
              )}
            </div>
            <div className="flex items-center gap-2">
              <Filter className={`w-4 h-4 ${sText}`} />
              <select
                className={`${inputCls} min-w-[200px]`}
                value={statusFilter}
                onChange={e => setStatusFilter(e.target.value)}
              >
                <option value="all">Todos los estados</option>
                {statusOptions.map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>
            <button
              onClick={loadOrders}
              className={`p-2 rounded-lg border transition-colors ${borderCls} ${isDark ? 'hover:bg-[#35363a]' : 'hover:bg-gray-50'}`}
              title="Recargar"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            </button>
          </div>
        </div>

        {/* Table */}
        <div className={`${cardCls} overflow-hidden`}>
          {loading ? (
            <div className="flex flex-col items-center justify-center py-20">
              <RefreshCw className="w-8 h-8 animate-spin text-blue-500 mb-3" />
              <p className={`text-sm ${sText}`}>Cargando POs...</p>
            </div>
          ) : filteredOrders.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-20">
              <Truck className={`w-12 h-12 mb-3 opacity-30 ${sText}`} />
              <p className={`font-medium ${sText}`}>No se encontraron POs</p>
              <p className={`text-sm mt-1 ${sText}`}>
                {searchTerm || statusFilter !== 'all' ? 'Ajustá los filtros' : 'Creá una nueva PO Individual'}
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className={`border-b ${borderCls} ${isDark ? 'bg-[#35363a]' : 'bg-gray-50'}`}>
                    {['Código PO', 'Full Order', 'Cliente', 'Comerciante', 'Fecha envío',
                      'FTY PI Status', 'Client Payment', 'Days to LSD', 'Total FOB', 'Margen', 'Acciones'
                    ].map(h => (
                      <th key={h} className={`px-3 py-3 text-left text-xs font-semibold uppercase tracking-wider ${sText}`}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {filteredOrders.map((order, idx) => {
                    const statusColor = FTY_PI_STATUS_COLORS[order.fty_pi_status] || { bg: 'bg-gray-100 text-gray-700 border-gray-300', dot: 'bg-gray-400' };
                    const daysToLsd = order.days_to_lsd ?? (order.shipping_date
                      ? diffDays(order.shipping_date, new Date().toISOString().split('T')[0])
                      : null);
                    return (
                      <motion.tr
                        key={order.id}
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ delay: idx * 0.02 }}
                        className={`border-b ${borderCls} ${isDark ? 'hover:bg-[#35363a]' : 'hover:bg-gray-50'} transition-colors cursor-pointer`}
                        onClick={() => openPO(order.id)}
                      >
                        <td className={`px-3 py-3 font-mono text-xs font-medium ${pText}`}>
                          {order.po_code || `#${order.id}`}
                        </td>
                        <td className={`px-3 py-3 ${pText}`}>{order.full_order_code || '-'}</td>
                        <td className={`px-3 py-3 max-w-[160px] truncate ${pText}`}>{order.client_name || '-'}</td>
                        <td className={`px-3 py-3 ${pText}`}>{order.buyer || '-'}</td>
                        <td className={`px-3 py-3 whitespace-nowrap ${pText}`}>{fmtDate(order.shipping_date)}</td>
                        <td className="px-3 py-3">
                          <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium border ${statusColor.bg}`}>
                            <span className={`w-1.5 h-1.5 rounded-full ${statusColor.dot}`} />
                            {getChoiceLabel('fty_pi_status', order.fty_pi_status)}
                          </span>
                        </td>
                        <td className={`px-3 py-3 text-xs ${pText}`}>{getChoiceLabel('client_payment', order.client_payment)}</td>
                        <td className="px-3 py-3 text-center">
                          {daysToLsd !== null ? (
                            <span className={`text-xs font-medium ${daysToLsd < 0 ? 'text-red-500' : daysToLsd <= 7 ? 'text-amber-500' : pText}`}>
                              {daysToLsd}
                            </span>
                          ) : '-'}
                        </td>
                        <td className={`px-3 py-3 text-right font-medium whitespace-nowrap ${pText}`}>
                          USD {fmtN(order.total_fob_price)}
                        </td>
                        <td className={`px-3 py-3 text-right ${pText}`}>
                          {order.margin != null ? `${fmtN(order.margin)} %` : '-'}
                        </td>
                        <td className="px-3 py-3" onClick={e => e.stopPropagation()}>
                          <div className="flex items-center gap-1">
                            <button
                              onClick={() => openPO(order.id)}
                              className={`p-1.5 rounded-lg transition-colors ${isDark ? 'hover:bg-[#35363a]' : 'hover:bg-gray-100'}`}
                              title="Ver / Editar"
                            >
                              <Eye className="w-4 h-4 text-blue-500" />
                            </button>
                            <button
                              onClick={() => setDeleteTarget(order)}
                              className={`p-1.5 rounded-lg transition-colors ${isDark ? 'hover:bg-[#35363a]' : 'hover:bg-gray-100'}`}
                              title="Eliminar"
                            >
                              <Trash2 className="w-4 h-4 text-red-500" />
                            </button>
                          </div>
                        </td>
                      </motion.tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </motion.div>

      {/* Delete confirm */}
      <AnimatePresence>
        {deleteTarget && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50"
            onClick={() => setDeleteTarget(null)}
          >
            <motion.div
              initial={{ scale: 0.95 }} animate={{ scale: 1 }} exit={{ scale: 0.95 }}
              className={`${cardCls} w-full max-w-sm p-6 text-center`}
              onClick={e => e.stopPropagation()}
            >
              <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-4">
                <Trash2 className="w-6 h-6 text-red-600" />
              </div>
              <h3 className={`text-lg font-semibold mb-2 ${pText}`}>Eliminar PO</h3>
              <p className={`text-sm mb-5 ${sText}`}>
                ¿Eliminar la PO <span className="font-medium">"{deleteTarget.po_code || deleteTarget.id}"</span>?
                Esta acción no se puede deshacer.
              </p>
              <div className="flex justify-center gap-3">
                <button
                  onClick={() => setDeleteTarget(null)}
                  className={`px-4 py-2 rounded-lg text-sm font-medium border ${borderCls} ${isDark ? 'hover:bg-[#35363a]' : 'hover:bg-gray-50'}`}
                >
                  Cancelar
                </button>
                <button
                  onClick={handleDelete}
                  className="px-4 py-2 bg-red-600 text-white rounded-lg text-sm font-medium hover:bg-red-700"
                >
                  Eliminar
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default PurchaseOrderManager;
